import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2 } from "lucide-react";
import type { ManagedUser, EditUserFormData } from "./types";
import { validateUsername } from "../../lib/username-validation";
import { OrgCombobox } from "./OrgCombobox";
import { CoTeachingField } from "./CoTeachingField";
import { GradeSelect } from "./GradeSelect";

type EditUserDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  user: ManagedUser | null;
  formData: EditUserFormData;
  onFormDataChange: (data: EditUserFormData) => void;
  onSubmit: (event: React.FormEvent) => void;
  isSaving: boolean;
  onCoTeacherCountChange?: (userId: string, count: number) => void;
};

export const EditUserDialog = ({
  isOpen,
  onOpenChange,
  user,
  formData,
  onFormDataChange,
  onSubmit,
  isSaving,
  onCoTeacherCountChange,
}: EditUserDialogProps) => {
  const [usernameError, setUsernameError] = useState("");

  // Co-teaching is shown only for teacher accounts that have a teacher row.
  const teacherId =
    user?.account_role === "TEACHER" ? user.teacherId ?? null : null;
  const isStudent = user?.account_role === "STUDENT";

  if (!user) return null;

  const handleUsernameChange = (value: string) => {
    onFormDataChange({ ...formData, username: value });
    // Clear error while typing (re-validate on blur)
    if (usernameError) {
      const err = validateUsername(value, false);
      setUsernameError(err);
    }
  };

  const handleUsernameBlur = () => {
    setUsernameError(validateUsername(formData.username, false));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const err = validateUsername(formData.username);
    setUsernameError(err);
    if (err) return;
    onSubmit(event);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information for {user.email}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                type="text"
                value={formData.name}
                onChange={(e) =>
                  onFormDataChange({ ...formData, name: e.target.value })
                }
                placeholder="John Doe"
              />
              <p className="text-xs text-muted-foreground">
                The user's full name
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  onFormDataChange({ ...formData, email: e.target.value })
                }
                placeholder="john@example.com"
                required
              />
              <p className="text-xs text-muted-foreground">
                The user's email address (used for login)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-username">Username</Label>
              <Input
                id="edit-username"
                type="text"
                value={formData.username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                onBlur={handleUsernameBlur}
                placeholder="johndoe"
              />
              {usernameError ? (
                <p className="text-xs text-destructive">{usernameError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Letters, numbers, underscores, and periods only.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-displayUsername">Display Name</Label>
              <Input
                id="edit-displayUsername"
                type="text"
                value={formData.displayUsername}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    displayUsername: e.target.value,
                  })
                }
                placeholder="John D."
              />
              <p className="text-xs text-muted-foreground">
                The name shown publicly in the app
              </p>
            </div>
            {isStudent && (
              <div className="space-y-2">
                <Label htmlFor="edit-grade">Grade</Label>
                <GradeSelect
                  id="edit-grade"
                  value={formData.grade}
                  onChange={(grade) =>
                    onFormDataChange({ ...formData, grade })
                  }
                />
              </div>
            )}
            <div className="space-y-2 sm:col-span-2">
              <Label>Organization</Label>
              <OrgCombobox
                value={formData.organizationId}
                onChange={(organizationId) =>
                  onFormDataChange({ ...formData, organizationId })
                }
              />
              <p className="text-xs text-muted-foreground">
                The organization this user belongs to
              </p>
            </div>
            {teacherId != null ? (
              <div className="sm:col-span-2">
                <CoTeachingField
                  key={teacherId}
                  teacherId={teacherId}
                  organizationId={formData.organizationId}
                  initialOrganizationId={user.organizationId ?? undefined}
                  onCountChange={(count) =>
                    onCoTeacherCountChange?.(user.id, count)
                  }
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
